import React, { useState, useRef, useEffect, useMemo } from 'react';
import { toolboxService } from '../../../services/api';

const Step3_Analyze = ({ scrapeResults, urlRows, urls, languages, selectedLanguages, isMultiMode, instruction, fields, onComplete, onBack }) => {
  const [progress, setProgress] = useState({});
  const [results, setResults] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const isMountedRef = useRef(true);
  const abortRef = useRef(null);

  const contentByUrl = useMemo(() => {
    const map = {};
    scrapeResults.forEach((r) => {
      if (r.success && r.content) map[r.url] = r.content;
    });
    return map;
  }, [scrapeResults]);

  const selectedLangsList = useMemo(() => languages.filter((l) => selectedLanguages.includes(l.code)), [languages, selectedLanguages]);

  const rowsToAnalyze = useMemo(() => {
    if (!isMultiMode) {
      return scrapeResults
        .filter((r) => r.success && r.content)
        .map((r) => ({
          items: [{ url: r.url, content: r.content, language: selectedLanguages[0] || 'en_US' }],
          primaryUrl: r.url,
        }));
    }
    return urlRows
      .map((row) => {
        const items = [];
        selectedLangsList.forEach((l) => {
          const url = row[l.code];
          if (url && contentByUrl[url]) {
            items.push({ url, content: contentByUrl[url], language: l.code });
          }
        });
        return { items, primaryUrl: items[0]?.url };
      })
      .filter((r) => r.items.length > 0);
  }, [isMultiMode, scrapeResults, urlRows, selectedLangsList, contentByUrl, selectedLanguages]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const runAnalyze = async () => {
    if (rowsToAnalyze.length === 0) return;
    setIsAnalyzing(true);
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    rowsToAnalyze.forEach((r, i) => {
      setProgress((p) => ({ ...p, [r.primaryUrl]: { status: 'pending', index: i + 1, total: rowsToAnalyze.length } }));
    });

    const out = [];
    for (let i = 0; i < rowsToAnalyze.length; i++) {
      const row = rowsToAnalyze[i];
      const key = row.primaryUrl;
      if (!isMountedRef.current || signal?.aborted) break;
      setProgress((p) => ({ ...p, [key]: { ...p[key], status: 'processing' } }));
      try {
        const data = await toolboxService.flexibleExtractAnalyzeGrouped(
          row.items,
          instruction,
          fields,
          signal
        );
        if (!isMountedRef.current || signal?.aborted) break;
        if (data.success && data.data) {
          setProgress((p) => ({ ...p, [key]: { status: 'success', index: i + 1, total: rowsToAnalyze.length } }));
          out.push({ url: key, success: true, data: data.data });
        } else {
          setProgress((p) => ({ ...p, [key]: { status: 'error', index: i + 1, total: rowsToAnalyze.length, error: data.error || 'No data' } }));
          out.push({ url: key, success: false, error: data.error || 'No data' });
        }
      } catch (err) {
        if (!isMountedRef.current || signal?.aborted) break;
        const msg = err.response?.data?.detail || err.message || 'Request failed';
        setProgress((p) => ({ ...p, [key]: { status: 'error', index: i + 1, total: rowsToAnalyze.length, error: msg } }));
        out.push({ url: key, success: false, error: msg });
      }
    }

    if (isMountedRef.current && !signal?.aborted) {
      setResults(out);
      setIsAnalyzing(false);
    }
  };

  const getIcon = (status) => {
    if (status === 'processing') {
      return (
        <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      );
    }
    if (status === 'success') {
      return (
        <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      );
    }
    return (
      <svg className="h-5 w-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    );
  };

  const completed = Object.values(progress).filter((p) => p.status === 'success' || p.status === 'error').length;
  const successCount = Object.values(progress).filter((p) => p.status === 'success').length;
  const total = rowsToAnalyze.length;
  const done = completed === total && total > 0;

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Step 3: Analyze</h2>
        <p className="text-gray-600">Run LLM extraction per row. Rows with multiple languages are analyzed together.</p>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <p className="text-sm text-gray-700">
          {rowsToAnalyze.length} row{rowsToAnalyze.length !== 1 ? 's' : ''} ready ({rowsToAnalyze.filter((r) => r.items.length > 1).length} multi-language).
        </p>
        {!isAnalyzing && results.length === 0 && (
          <button
            type="button"
            onClick={runAnalyze}
            disabled={rowsToAnalyze.length === 0}
            className="mt-3 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium"
          >
            Analyze
          </button>
        )}
      </div>

      {isAnalyzing && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex justify-between items-center mb-2">
            <span className="font-medium text-blue-900">Progress</span>
            <span className="text-blue-700">{completed} / {total} completed</span>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all"
              style={{ width: total ? `${(completed / total) * 100}%` : 0 }}
            />
          </div>
          <p className="text-sm text-blue-700 mt-1">{successCount} successful, {completed - successCount} failed</p>
        </div>
      )}

      <div className="space-y-2">
        {rowsToAnalyze.map((row, i) => {
          const p = progress[row.primaryUrl] || { status: 'pending', index: i + 1, total, error: null };
          return (
            <div
              key={i}
              className={`flex items-center gap-3 p-3 border rounded-md ${
                p.status === 'success' ? 'bg-green-50 border-green-200' :
                p.status === 'error' ? 'bg-red-50 border-red-200' :
                p.status === 'processing' ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'
              }`}
            >
              {getIcon(p.status)}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">[{p.index}/{p.total}] Row {p.index}: {row.items.length} language(s)</div>
                {p.error && <div className="text-xs text-red-600 mt-0.5">{p.error}</div>}
              </div>
            </div>
          );
        })}
      </div>

      {done && (
        <div className="flex justify-between">
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-3 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 font-medium"
          >
            Back
          </button>
          <button
            type="button"
            onClick={() => onComplete(results)}
            disabled={successCount === 0}
            className="px-8 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium"
          >
            {successCount === 0 ? 'No data' : `Continue to Preview (${successCount})`}
          </button>
        </div>
      )}
    </div>
  );
};

export default Step3_Analyze;
